import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import Header from "@/components/Header";
import PublicFooter from "@/components/PublicFooter";
import { Check, Zap, Shield, Users } from "lucide-react";

const PricingPage = () => {
  const plans = [
    {
      name: "Free",
      price: "€0",
      period: "forever",
      description: "Perfect for trying Hireun",
      features: [
        "5 interview prep generations",
        "CV and job description analysis",
        "Basic interview questions",
        "STAR method responses",
        "Email support"
      ],
      cta: "Get Started Free",
      href: "/login",
      highlighted: false
    },
    {
      name: "Premium",
      price: "€3.99",
      period: "/month",
      description: "For active job seekers",
      features: [
        "Unlimited interview preps",
        "Advanced AI refinement chat",
        "Company & sector intelligence",
        "Salary benchmarking",
        "Networking insights",
        "Priority generation",
        "No ads"
      ],
      cta: "Start Premium",
      href: "/login",
      highlighted: true
    }
  ];

  return (
    <>
      <Helmet>
        <title>Pricing - Interview Preparation Plans | Hireun</title>
        <meta name="description" content="Choose the right Hireun plan for your interview preparation needs. Free and premium options available." />
        <link rel="canonical" href="https://hireun.com/pricing" />
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        <Header />

        {/* Hero Section */}
        <section className="bg-gradient-to-br from-blue-600 to-indigo-700 text-white py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Simple, Transparent Pricing
            </h1>
            <p className="text-xl text-blue-100 max-w-2xl mx-auto">
              Start free and upgrade when you're ready. No hidden fees, no surprises.
            </p>
          </div>
        </section>

        {/* Pricing Cards */}
        <section className="py-16 -mt-8">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-2 gap-8">
              {plans.map((plan) => (
                <div
                  key={plan.name}
                  className={`bg-white rounded-2xl shadow-lg p-8 ${
                    plan.highlighted 
                      ? "ring-2 ring-blue-600 relative" 
                      : "border border-gray-200"
                  }`}
                >
                  {plan.highlighted && (
                    <span className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-blue-600 text-white text-sm font-medium px-4 py-1 rounded-full">
                      Most Popular
                    </span>
                  )}
                  
                  <div className="text-center mb-6">
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h2>
                    <div className="flex items-baseline justify-center">
                      <span className="text-4xl font-bold text-gray-900">{plan.price}</span>
                      <span className="text-gray-500 ml-1">{plan.period}</span>
                    </div>
                    <p className="text-gray-600 mt-2">{plan.description}</p>
                  </div>

                  <ul className="space-y-4 mb-8">
                    {plan.features.map((feature, index) => (
                      <li key={index} className="flex items-start">
                        <Check className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <Link
                    to={plan.href}
                    className={`block text-center py-3 px-6 rounded-lg font-medium transition-colors ${
                      plan.highlighted
                        ? "bg-blue-600 text-white hover:bg-blue-700"
                        : "bg-gray-100 text-gray-900 hover:bg-gray-200"
                    }`}
                  >
                    {plan.cta}
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Trust Signals */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Why Choose Hireun?
              </h2>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Zap className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">AI-Powered</h3>
                <p className="text-gray-600">
                  Advanced AI generates personalized interview content based on your CV and target role.
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Shield className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Privacy First</h3>
                <p className="text-gray-600">
                  Your CV is encrypted and never shared. Delete your data anytime.
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="w-8 h-8 text-purple-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Expert Insights</h3>
                <p className="text-gray-600">
                  Built with real hiring manager feedback to help you succeed.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
              Frequently Asked Questions
            </h2>

            <div className="space-y-6">
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <h3 className="font-semibold text-gray-900 mb-2">
                  Can I cancel my subscription anytime?
                </h3>
                <p className="text-gray-600">
                  Yes, you can cancel your Premium subscription at any time. You'll continue to have 
                  access until the end of your billing period.
                </p>
              </div>

              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <h3 className="font-semibold text-gray-900 mb-2">
                  What happens when I use all my free generations?
                </h3>
                <p className="text-gray-600">
                  You can upgrade to Premium for unlimited generations, or wait for your monthly 
                  allowance to reset.
                </p>
              </div>

              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <h3 className="font-semibold text-gray-900 mb-2">
                  Is my data secure?
                </h3>
                <p className="text-gray-600">
                  Yes, we take privacy seriously. Your CV and personal data are encrypted and never 
                  shared with third parties. You can delete your data at any time.
                </p>
              </div>

              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <h3 className="font-semibold text-gray-900 mb-2">
                  Do you offer refunds?
                </h3>
                <p className="text-gray-600">
                  If you're not satisfied with Premium within the first 7 days, contact us for a 
                  full refund.
                </p>
              </div>
            </div>
          </div>
        </section>

        <PublicFooter />
      </div>
    </>
  );
};

export default PricingPage;
